import { lbBrand } from '@fuse/colors';
import NavLinkAdapter from '@fuse/core/NavLinkAdapter';
import FuseUtils from '@fuse/utils';
import ListSubheader from '@material-ui/core/ListSubheader';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { navbarCloseMobile } from 'app/store/fuse/navbarSlice';
import FuseNavItem from '../FuseNavItem';

const useStyles = makeStyles(() => ({
  item: props => ({
    height: 40,
    width: 'calc(100% - 16px)',
    borderRadius: '0 20px 20px 0',
    paddingRight: 12,
    paddingLeft: props.itemPadding > 80 ? 80 : props.itemPadding,
    position: 'relative',
    // Section captions sit back in grey; black is left to the item labels.
    color: lbBrand.navSectionLabel,
    '&.active > .list-subheader-text': {
      fontWeight: 700,
    },
    // Rule between top-level sections, drawn inset in the gap above the caption
    // so it reads as a divider rather than a full-bleed edge. The first group
    // needs none — the user block's own border already sits above it, and
    // :first-child being DOM-based means a permission-hidden first group
    // correctly promotes the next one. ::after, not ::before: the folded-navbar
    // styles already own :before on .list-subheader.
    ...(props.isTopLevel && {
      '&:not(:first-child)': {
        width: '100%',
        marginTop: 20,
        '&::after': {
          content: '""',
          position: 'absolute',
          top: -10,
          left: 24,
          right: 24,
          borderTop: `1px solid ${lbBrand.navDivider}`,
        },
      },
    }),
  }),
}));

function FuseNavVerticalGroup(props) {
  const userRole = useSelector(({ auth }) => auth.user.role);
  const dispatch = useDispatch();

  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down('md'));
  const { item, nestedLevel } = props;
  const classes = useStyles({
    itemPadding: nestedLevel > 0 ? 40 + nestedLevel * 16 : 24,
    isTopLevel: nestedLevel === 0,
  });
  const { t } = useTranslation();

  const hasPermission = useMemo(() => FuseUtils.hasPermission(item.auth, userRole), [item.auth, userRole]);

  if (!hasPermission) {
    return null;
  }

  return (
    <>
      <ListSubheader
        disableSticky
        className={clsx(classes.item, 'list-subheader flex items-center', !item.url && 'cursor-default')}
        onClick={() => mdDown && dispatch(navbarCloseMobile())}
        component={item.url ? NavLinkAdapter : 'li'}
        to={item.url}
        role="button"
      >
        <span className="list-subheader-text uppercase text-12">
          {item.translate ? t(`navigation.${item.translate}`) : item.title}
        </span>
      </ListSubheader>

      {item.children && (
        <>
          {item.children.map(_item => (
            <FuseNavItem key={_item.id} type={`vertical-${_item.type}`} item={_item} nestedLevel={nestedLevel} />
          ))}
        </>
      )}
    </>
  );
}

FuseNavVerticalGroup.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    children: PropTypes.array,
  }),
};

FuseNavVerticalGroup.defaultProps = {};

const NavVerticalGroup = withRouter(React.memo(FuseNavVerticalGroup));

export default NavVerticalGroup;
